import { useState, useCallback, useEffect, useRef } from "react";
import { useConversation } from "@elevenlabs/react";
import { Phone, PhoneOff, Mic, MicOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGENT_ID = process.env.REACT_APP_ELEVENLABS_AGENT_ID;

const AGENT_PROFILES = {
  default: { id: AGENT_ID, name: "Elderra Agent" },
  games: { id: process.env.REACT_APP_ELEVENLABS_GAMES_AGENT_ID, name: "Daily Games" },
};

export default function VoiceCallButton({ sessionId }) {
  const [callActive, setCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [status, setStatus] = useState("idle");
  const [activeProfile, setActiveProfile] = useState(AGENT_PROFILES.default);
  const activeProfileKeyRef = useRef("default");
  const sessionIdRef = useRef(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const conversation = useConversation({
    onConnect: () => setStatus("connected"),
    onDisconnect: () => {
      setStatus("idle");
      setCallActive(false);
      setIsMuted(false);
      // Log a Daily Games play so the streak counter (web + family dashboard) updates.
      if (activeProfileKeyRef.current === "games" && sessionIdRef.current) {
        axios.post(`${API}/games/log`, { session_id: sessionIdRef.current })
          .then(() => window.dispatchEvent(new Event("elderra:game-logged")))
          .catch(() => {});
      }
    },
    onError: (err) => {
      console.error("ElevenLabs error:", err);
      setStatus("idle");
      setCallActive(false);
    },
    onModeChange: ({ mode }) => {
      setStatus(mode === "listening" ? "listening" : "speaking");
    },
  });

  const startCall = useCallback(async (profileKey = "default") => {
    const profile = AGENT_PROFILES[profileKey] || AGENT_PROFILES.default;
    if (!profile.id) return;
    setActiveProfile(profile);
    activeProfileKeyRef.current = profileKey;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setCallActive(true);
      setStatus("connecting");
      await conversation.startSession({ agentId: profile.id });
    } catch (err) {
      console.error("Call start failed:", err);
      setCallActive(false);
      setStatus("idle");
    }
  }, [conversation]);

  // Allow other parts of the app (e.g. the "Health Advice" or "Daily Games"
  // service cards) to trigger a voice call with a specific agent profile
  // without prop-drilling through Header. detail.profile picks the agent.
  useEffect(() => {
    const handler = (e) => { if (!callActive) startCall(e?.detail?.profile); };
    window.addEventListener("elderra:start-voice-call", handler);
    return () => window.removeEventListener("elderra:start-voice-call", handler);
  }, [callActive, startCall]);

  const endCall = useCallback(async () => {
    try {
      await conversation.endSession();
    } catch (err) {
      console.error("Failed to end voice session cleanly:", err);
    }
    setCallActive(false);
    setStatus("idle");
    setIsMuted(false);
  }, [conversation]);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      conversation.setVolume({ volume: 1 });
    } else {
      conversation.setVolume({ volume: 0 });
    }
    setIsMuted(!isMuted);
  }, [conversation, isMuted]);

  if (!AGENT_ID) return null;

  // Not in call - show the call button
  if (!callActive) {
    return (
      <button
        onClick={() => startCall("default")}
        className="p-2 md:p-2.5 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Voice call with agent"
        data-testid="voice-call-btn"
        title="Talk to Elderra Agent"
      >
        <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
      </button>
    );
  }

  // In-call overlay
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="voice-call-overlay">
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative flex flex-col items-center gap-6 p-8 max-w-sm w-full mx-4">
        {/* Pulsating avatar */}
        <div className={`relative ${status === "speaking" ? "animate-pulse" : ""}`}>
          <div className={`w-28 h-28 rounded-full border-4 flex items-center justify-center ${
            status === "speaking" ? "border-green-400 bg-green-900/40" :
            status === "listening" ? "border-blue-400 bg-blue-900/40" :
            "border-white/30 bg-white/10"
          }`}>
            <img
              src="/elderra-logo.png"
              alt="Elderra Agent"
              className="w-20 h-20 rounded-full object-cover"
            />
          </div>
          {status === "listening" && (
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              Listening...
            </span>
          )}
          {status === "speaking" && (
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              Speaking...
            </span>
          )}
          {status === "connecting" && (
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-yellow-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              Connecting...
            </span>
          )}
        </div>

        <div className="text-center">
          <h2 className="text-white text-xl font-semibold" data-testid="voice-call-title">{activeProfile.name}</h2>
          <p className="text-white/60 text-sm mt-1">
            {status === "connecting" ? "Connecting..." :
             status === "listening" ? "Listening to you..." :
             status === "speaking" ? "Agent is speaking..." :
             "Connected"}
          </p>
        </div>

        {/* Call controls */}
        <div className="flex items-center gap-6 mt-4">
          {/* Mute button */}
          <button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              isMuted ? "bg-red-500/30 text-red-400" : "bg-white/10 text-white hover:bg-white/20"
            }`}
            data-testid="mute-btn"
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </button>

          {/* End call button */}
          <button
            onClick={endCall}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center text-white transition-colors shadow-lg shadow-red-600/30"
            data-testid="end-call-btn"
            aria-label="End call"
          >
            <PhoneOff className="w-7 h-7" />
          </button>
        </div>
      </div>
    </div>
  );
}
